import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Badge,
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  FormField,
  FullscreenSpinner,
  Input,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  type Column,
} from '@shared/ui';
import {
  adminApi,
  type AdminInviteStatus,
  type AdminInviteView,
  type AdminUserView,
} from '@entities/admin';
import { useSessionStore } from '@entities/session';
import {
  useInviteAdmin,
  useRevokeAdmin,
  useRevokeInvite,
} from '@features/admin-management';
import { formatDateTime } from '@shared/lib/format';

const inviteSchema = z.object({
  email: z.string().email(),
  displayName: z.string().max(120).optional().or(z.literal('')),
});

type InviteValues = z.infer<typeof inviteSchema>;

const inviteStatusVariant: Record<AdminInviteStatus, 'secondary' | 'warning' | 'success' | 'destructive'> = {
  PENDING: 'warning',
  ACCEPTED: 'success',
  REVOKED: 'secondary',
  EXPIRED: 'destructive',
};

export function AdminsPage() {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [revokingAdmin, setRevokingAdmin] = useState<AdminUserView | null>(null);
  const [revokingInvite, setRevokingInvite] = useState<AdminInviteView | null>(null);
  const [inviteStatus, setInviteStatus] = useState<AdminInviteStatus>('PENDING');

  const currentUserId = useSessionStore((s) => s.identity?.id);

  const adminsQ = useQuery({
    queryKey: ['admins'],
    queryFn: () => adminApi.list(),
  });

  const invitesQ = useQuery({
    queryKey: ['admin-invites', inviteStatus],
    queryFn: () => adminApi.listInvites(inviteStatus, 0, 100),
  });

  const invite = useInviteAdmin();
  const revokeAdmin = useRevokeAdmin();
  const revokeInvite = useRevokeInvite();

  const ADMIN_COLUMNS: Column<AdminUserView>[] = [
    { key: 'email', header: 'Email', cell: (r) => r.email },
    { key: 'name', header: 'Имя', cell: (r) => r.displayName ?? '—' },
    {
      key: 'roles',
      header: 'Роли',
      cell: (r) => (
        <div className="flex flex-wrap gap-1">
          {r.roles.map((role) => (
            <Badge key={role} variant="outline" className="font-mono text-xs">
              {role}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      key: 'blocked',
      header: 'Status',
      cell: (r) =>
        r.blocked ? <Badge variant="destructive">blocked</Badge> : <Badge variant="success">active</Badge>,
    },
    { key: 'created', header: 'Создан', cell: (r) => formatDateTime(r.createdAt) },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      cell: (r) => (
        <Button
          size="sm"
          variant="ghost"
          disabled={r.id === currentUserId}
          title={r.id === currentUserId ? 'Нельзя снять роль с себя' : undefined}
          onClick={() => setRevokingAdmin(r)}
        >
          revoke ADMIN
        </Button>
      ),
    },
  ];

  const INVITE_COLUMNS: Column<AdminInviteView>[] = [
    { key: 'email', header: 'Email', cell: (r) => r.email },
    { key: 'name', header: 'Имя', cell: (r) => r.displayName ?? '—' },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => <Badge variant={inviteStatusVariant[r.status]}>{r.status}</Badge>,
    },
    { key: 'expires', header: 'Истекает', cell: (r) => formatDateTime(r.expiresAt) },
    { key: 'created', header: 'Создан', cell: (r) => formatDateTime(r.createdAt) },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      cell: (r) =>
        r.status === 'PENDING' ? (
          <Button size="sm" variant="ghost" onClick={() => setRevokingInvite(r)}>
            revoke
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Администраторы</h1>
        <Button onClick={() => setInviteOpen(true)}>+ Пригласить</Button>
      </div>

      <Tabs defaultValue="admins">
        <TabsList>
          <TabsTrigger value="admins">Активные</TabsTrigger>
          <TabsTrigger value="invites">Приглашения</TabsTrigger>
        </TabsList>

        <TabsContent value="admins">
          <Card>
            <CardContent className="p-0">
              {adminsQ.isLoading ? (
                <FullscreenSpinner />
              ) : (
                <DataTable
                  rows={adminsQ.data ?? []}
                  columns={ADMIN_COLUMNS}
                  rowKey={(r) => r.id}
                  empty={<EmptyState title="Нет активных админов" />}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invites">
          <div className="mb-3 flex items-center gap-2">
            <select
              value={inviteStatus}
              onChange={(e) => setInviteStatus(e.target.value as AdminInviteStatus)}
              className="h-10 rounded-md border bg-background px-2 text-sm"
            >
              <option value="PENDING">PENDING</option>
              <option value="ACCEPTED">ACCEPTED</option>
              <option value="REVOKED">REVOKED</option>
              <option value="EXPIRED">EXPIRED</option>
            </select>
          </div>
          <Card>
            <CardContent className="p-0">
              {invitesQ.isLoading ? (
                <FullscreenSpinner />
              ) : (
                <DataTable
                  rows={invitesQ.data?.content ?? []}
                  columns={INVITE_COLUMNS}
                  rowKey={(r) => r.id}
                  empty={<EmptyState title="Приглашений нет" />}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <InviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        isPending={invite.isPending}
        onSubmit={(v) =>
          invite.mutate(
            { email: v.email, displayName: v.displayName || undefined },
            { onSuccess: () => setInviteOpen(false) },
          )
        }
      />

      <ConfirmDialog
        open={!!revokingAdmin}
        onOpenChange={(o) => !o && setRevokingAdmin(null)}
        title={`Снять роль ADMIN у ${revokingAdmin?.email}?`}
        description="Если у пользователя не останется ролей — он будет заблокирован."
        confirmLabel="Снять ADMIN"
        variant="destructive"
        isPending={revokeAdmin.isPending}
        onConfirm={() =>
          revokeAdmin.mutate(revokingAdmin!.id, { onSuccess: () => setRevokingAdmin(null) })
        }
      />

      <ConfirmDialog
        open={!!revokingInvite}
        onOpenChange={(o) => !o && setRevokingInvite(null)}
        title={`Отозвать приглашение ${revokingInvite?.email}?`}
        confirmLabel="Отозвать"
        variant="destructive"
        isPending={revokeInvite.isPending}
        onConfirm={() =>
          revokeInvite.mutate(revokingInvite!.id, { onSuccess: () => setRevokingInvite(null) })
        }
      />
    </div>
  );
}

function InviteDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (v: InviteValues) => void;
  isPending: boolean;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: '', displayName: '' },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Пригласить администратора</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={handleSubmit((v) => {
            onSubmit(v);
            reset();
          })}
          className="space-y-3"
        >
          <FormField label="Email" htmlFor="email" error={errors.email?.message}>
            <Input id="email" type="email" autoComplete="off" {...register('email')} />
          </FormField>
          <FormField label="Имя (опционально)" htmlFor="displayName" error={errors.displayName?.message}>
            <Input id="displayName" {...register('displayName')} />
          </FormField>
          <p className="text-xs text-muted-foreground">
            На email отправится ссылка с одноразовым токеном (действует 7 дней).
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Отмена
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? <Spinner /> : 'Пригласить'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
